import { Form, FormAnswer, Question } from '../../../../api/types/forms';
import { ChallengeTableInspectionsProgramsVersionsItem, ChallengeTableProgramVersionItem } from '../../../../api/types/dtos/challenge/ChallengeDto';
import { SelectedQuestion } from '../types/reportBuilder.types';

/**
 * Parse form answers from JSON string
 */
export function parseFormAnswers(value: string | null | undefined): FormAnswer[] {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed as FormAnswer[];
    }
    return [];
  } catch (e) {
    console.error('Error parsing form answers:', e);
    return [];
  }
}

/**
 * Extract unique form IDs from program version items
 * @param programVersionItems Array of program version items
 * @returns Array of unique form IDs
 */
export function extractFormIdsFromPVIs(programVersionItems: ChallengeTableProgramVersionItem[]): string[] {
  const formIdsSet = new Set<string>();
  
  programVersionItems.forEach(pvi => {
    if (pvi.formId) {
      formIdsSet.add(pvi.formId);
    }
  });
  
  return Array.from(formIdsSet);
}

/**
 * Create a mapping from program version item ID to form ID
 * @param programVersionItems Array of program version items
 * @returns Map of PVI ID to form ID
 */
export function createPviToFormIdMap(programVersionItems: ChallengeTableProgramVersionItem[]): Map<string, string> {
  const map = new Map<string, string>();
  
  programVersionItems.forEach(pvi => {
    if (pvi.formId) {
      map.set(pvi.id, pvi.formId);
    }
  });
  
  return map;
}

/**
 * Build dependency tree for questions
 */
export function buildQuestionDependencyTree(questions: Question[]): Map<string, Question[]> {
  const dependencyMap = new Map<string, Question[]>();
  
  questions.forEach(question => {
    if (question.display_logic.condition_type === 'for_each' && question.display_logic.source_multiselect_id) {
      const parentId = question.display_logic.source_multiselect_id;
      if (!dependencyMap.has(parentId)) {
        dependencyMap.set(parentId, []);
      }
      dependencyMap.get(parentId)!.push(question);
    }
  });
  
  return dependencyMap;
}

/**
 * Get all questions from a form, including nested FOR_EACH questions
 */
export function getAllQuestions(form: Form | null): Question[] {
  if (!form || !form.questions) return [];
  return form.questions;
}

/**
 * Group answers by question ID
 */
export function groupAnswersByQuestionId(answers: FormAnswer[]): Map<string, FormAnswer[]> {
  const grouped = new Map<string, FormAnswer[]>();
  
  answers.forEach(answer => {
    if (!grouped.has(answer.questionId)) {
      grouped.set(answer.questionId, []);
    }
    grouped.get(answer.questionId)!.push(answer);
  });
  
  return grouped;
}

/**
 * Group answers by FOR_EACH context value
 */
export function groupAnswersByForEachContext(
  answers: FormAnswer[],
  questionId: string
): Map<string, FormAnswer[]> {
  const grouped = new Map<string, FormAnswer[]>();
  
  answers
    .filter(a => a.questionId === questionId)
    .forEach(answer => {
      const contextValue = answer.forEachContextValue || 'default';
      if (!grouped.has(contextValue)) {
        grouped.set(contextValue, []);
      }
      grouped.get(contextValue)!.push(answer);
    });
  
  return grouped;
}

/**
 * Get answers for a specific question from an inspection item
 */
export function getQuestionAnswers(
  item: ChallengeTableInspectionsProgramsVersionsItem,
  questionId: string
): FormAnswer[] {
  const answers = parseFormAnswers(item.value);
  return answers.filter(a => a.questionId === questionId);
}

/**
 * Get all answers for a program version item
 */
export function getAllAnswersForItem(
  item: ChallengeTableInspectionsProgramsVersionsItem
): FormAnswer[] {
  return parseFormAnswers(item.value);
}

/**
 * Find parent question for a FOR_EACH dependent question
 */
export function findParentQuestion(
  question: Question,
  allQuestions: Question[]
): Question | null {
  if (question.display_logic.condition_type === 'for_each' && question.display_logic.source_multiselect_id) {
    return allQuestions.find(q => q.id === question.display_logic.source_multiselect_id) || null;
  }
  return null;
}

/**
 * Find all child questions for a parent question
 */
export function findChildQuestions(
  parentQuestionId: string,
  allQuestions: Question[]
): Question[] {
  return allQuestions.filter(
    q => q.display_logic.condition_type === 'for_each' && 
         q.display_logic.source_multiselect_id === parentQuestionId
  );
}

/**
 * Build selected question with metadata
 */
export function buildSelectedQuestion(
  question: Question,
  programVersionItemId: string,
  programVersionItem: any,
  parentQuestionId?: string,
  forEachContextValue?: string
): SelectedQuestion {
  return {
    programVersionItemId,
    questionId: question.id,
    question,
    programVersionItem,
    parentQuestionId,
    forEachContextValue,
  };
}
